import {
	DocuSignEnvelopeTrackingDto,
	DocumentTemplate,
	EmbeddedSigningResponse,
	SendConventionRequest,
	SendConventionResponse,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import {
	createDocumentTemplate,
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../queries/files.queries";
import { societiesQueryKeys } from "../query-keys/societies.query-keys";

export type UpsertTemplateVariables = {
	name: string;
	entityModel: string;
	templateType: string;
	file: File;
};

export const useUpsertDocumentTemplate = () => {
	const queryClient = useQueryClient();
	return useMutation<DocumentTemplate, Error, UpsertTemplateVariables>({
		mutationFn: async ({ name, entityModel, templateType, file }) => {
			const signedPutUrlResponseData = await getSignedPutUrl({
				bucket: "private",
				contentType: file.type,
			});
			const { signedUrl, key: fileKey } = signedPutUrlResponseData;

			await uploadFileToS3(signedUrl, file);

			const registerResponseData = await registerS3File({
				bucket: "private",
				key: fileKey,
				name: file.name,
				mimeType: file.type,
			});
			const { id: fileId } = registerResponseData;

			const createDocumentTemplateResponseData = await createDocumentTemplate({
				name,
				entityModel,
				templateType,
				fileId,
				fileKey,
			});
			return createDocumentTemplateResponseData;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["documentTemplates"] });
			toast.success("Modèle de document créé avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la création du modèle", {
				description: errorMessage,
			});
		},
	});
};

export const useRevertDocumentTemplateVersion = () => {
	const queryClient = useQueryClient();
	return useMutation<void, Error, string>({
		mutationFn: async (templateId: string) => {
			await privateAxios.delete(`/documents/templates/${templateId}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["documentTemplates"] });
			toast.success("Version du modèle supprimée avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression de la version", {
				description: errorMessage,
			});
		},
	});
};

export type UploadVersionVariables = {
	templateId: string;
	fileId: string;
	fileKey: string;
};

export const useUploadDocumentTemplateVersion = () => {
	const queryClient = useQueryClient();
	return useMutation<DocumentTemplate, Error, UploadVersionVariables>({
		mutationFn: async ({ templateId, fileId, fileKey }) => {
			const resp = await privateAxios.post(
				`/documents/templates/${templateId}/upload`,
				{ fileId, fileKey },
			);
			return resp.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["documentTemplates"] });
			toast.success("Nouvelle version du modèle téléchargée avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors du téléchargement de la nouvelle version", {
				description: errorMessage,
			});
		},
	});
};

export const useDeleteAllDocumentTemplateVersions = () => {
	const queryClient = useQueryClient();
	return useMutation<void, Error, string>({
		mutationFn: async (templateId: string) => {
			await privateAxios.delete(`/documents/templates/${templateId}/versions`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["documentTemplates"] });
			toast.success("Toutes les versions du modèle supprimées avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression des versions", {
				description: errorMessage,
			});
		},
	});
};

export type GenerateDocumentVariables = {
	entityModel: string;
	entityId: string;
	templateId: string;
};

export type GenerateDocumentResponse = {
	documentUrl: string;
};

export const useGenerateDocument = () => {
	return useMutation<
		GenerateDocumentResponse,
		Error,
		GenerateDocumentVariables
	>({
		mutationFn: async (variables) => {
			const resp = await privateAxios.post<GenerateDocumentResponse>(
				"/documents/generate",
				variables,
			);
			return resp.data;
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la génération du document", {
				description: errorMessage,
			});
		},
	});
};

export const useGenerateConvention = (societyId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: {
			templateId?: string;
			signataireDuneId?: number;
			format?: string;
		}) => {
			const response = await privateAxios.post<{
				documentUrl: string;
				documentId: number;
				fileId: string;
				message: string;
			}>("/documents/generate-convention", {
				societyId,
				templateId: data.templateId,
				signataireDuneId: data.signataireDuneId,
				format: data.format,
			});
			return response.data;
		},
		onSuccess: (_data) => {
			toast.success("Convention générée avec succès");
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerTasks(String(societyId)),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerDocuments(String(societyId)),
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la génération de la convention", {
				description: errorMessage,
			});
		},
	});
};

export const useSendConvention = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: SendConventionRequest) => {
			const response = await privateAxios.post<SendConventionResponse>(
				"/documents/send-convention",
				{
					societyId: Number(data.societyId),
					signatureMethod: data.signatureMethod,
					fileId: data.fileId,
				},
			);
			return response.data;
		},
		onSuccess: (data, variables) => {
			toast.success(data.message);
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerTasks(String(variables.societyId)),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerDocuments(
					String(variables.societyId),
				),
			});
		},
		onError: (error) => {
			// Handle special DocuSign consent error
			const errorResponse = error as {
				response?: {
					data?: {
						message?: string;
						consentUrl?: string;
						errorCode?: string;
					};
				};
			};

			const responseData = errorResponse?.response?.data;

			if (
				responseData?.errorCode === "DOCUSIGN_CONSENT_REQUIRED" &&
				responseData?.consentUrl
			) {
				toast.error(
					responseData.message || "Le consentement DocuSign est requis",
					{
						description: "Cliquez sur le bouton pour accorder le consentement",
						action: {
							label: "Accorder le consentement",
							onClick: () => {
								window.open(responseData.consentUrl, "_blank");
							},
						},
						duration: 10000,
					},
				);
			} else {
				const errorMessage = getErrorMessage(error);
				toast.error("Erreur lors de l'envoi de la convention", {
					description: errorMessage,
				});
			}
		},
	});
};

export const useGetEnvelopeTracking = (envelopeId: string) => {
	return useQuery<DocuSignEnvelopeTrackingDto>({
		queryKey: ["envelope-tracking", envelopeId],
		queryFn: async () => {
			const response = await privateAxios.get<DocuSignEnvelopeTrackingDto>(
				`/documents/envelope-tracking/${envelopeId}`,
			);
			return response.data;
		},
		enabled: !!envelopeId,
	});
};

export const useGetSigningUrl = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: { envelopeId: string; recipientId: string }) => {
			const response = await privateAxios.post<EmbeddedSigningResponse>(
				"/docusign/get-signing-url",
				data,
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["envelope-tracking"] });
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la récupération de l'URL de signature", {
				description: errorMessage,
			});
		},
	});
};
